import React, { useState, useCallback } from 'react';
import { API_URL, apiFetch } from '../config/api';

/**
 * Pre-flight Checklist UI — visual checklist displayed before mission start.
 * Calls POST /api/drones/:droneId/missions/:missionId/preflight for a dry-run.
 */
export default function PreflightChecklist({ droneId, missionId, onComplete, onCancel, onClose, onProceed, onForceProceed }) {
  const handleCancel = onCancel || onClose;
  const handleComplete = onProceed || onComplete;
  const handleForceComplete = onForceProceed;
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acknowledgedManual, setAcknowledgedManual] = useState([]);
  const [error, setError] = useState(null);

  const runChecklist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_URL}/drones/${droneId}/missions/${missionId}/preflight`, {
        method: 'POST',
        body: JSON.stringify({ acknowledgedManual }),
      });
      const data = await res.json();
      if (data.success !== false) {
        setResults(data.preflight || data);
      } else {
        setError(data.message || 'Preflight check failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    }
    setLoading(false);
  }, [droneId, missionId, acknowledgedManual]);

  const toggleManualCheck = (id) => {
    setAcknowledgedManual(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleProceed = () => {
    if (handleComplete) handleComplete(acknowledgedManual);
  };

  const getStatusIcon = (check) => {
    if (check.passed === true) return '✅';
    if (check.passed === false) return check.blocking ? '❌' : '⚠️';
    return '⏭️';
  };

  const getStatusColor = (check) => {
    if (check.passed === true) return '#22c55e';
    if (check.passed === false) return check.blocking ? 'var(--danger)' : 'var(--warning)';
    return '#6b7280';
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>Pre-flight Checklist</h3>
          <button onClick={handleCancel} style={styles.closeBtn}>&times;</button>
        </div>

        {!results && (
          <div style={styles.body}>
            <p style={styles.info}>
              Run automated pre-flight checks before starting mission. 
              Acknowledge manual checks below, then click "Run Checks".
            </p>

            <div style={styles.manualSection}>
              <h4 style={styles.sectionTitle}>Manual Checks</h4>
              {[
                { id: 'manual_props', label: 'Propellers secure and undamaged' },
                { id: 'manual_airframe', label: 'Airframe and arms secured' },
                { id: 'manual_servo', label: 'All servos/motors respond correctly' },
                { id: 'manual_area', label: 'Flight area clear of obstacles and people' },
                { id: 'manual_payload', label: 'Payload secured (if applicable)' },
              ].map(mc => (
                <label key={mc.id} style={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={acknowledgedManual.includes(mc.id)}
                    onChange={() => toggleManualCheck(mc.id)}
                    style={styles.checkbox}
                  />
                  <span>{mc.label}</span>
                </label>
              ))}
            </div>

            <button onClick={runChecklist} disabled={loading} style={styles.runBtn}>
              {loading ? 'Running checks...' : 'Run Checks'}
            </button>
            {error && <div style={styles.error}>{error}</div>}
          </div>
        )}

        {results && (
          <div style={styles.body}>
            {/* Summary */}
            <div style={{
              ...styles.summary,
              borderColor: results.passed ? '#22c55e' : 'var(--danger)',
              background: results.passed ? '#22c55e11' : '#ef444411',
            }}>
              <div style={{ fontSize: '20px' }}>{results.passed ? '✅' : '❌'}</div>
              <div>
                <strong>{results.passed ? 'All checks passed' : 'Some checks failed'}</strong>
                <div style={styles.summaryStats}>
                  {results.summary?.passed || 0} passed, {results.summary?.failed || 0} failed, 
                  {results.summary?.warnings || 0} warnings, {results.summary?.skipped || 0} skipped
                </div>
              </div>
            </div>

            {/* Blocking issues */}
            {results.blocking?.length > 0 && (
              <div style={styles.blockingBox}>
                <strong>Blocking issues:</strong>
                <ul style={styles.blockingList}>
                  {results.blocking.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}

            {/* Individual checks */}
            <div style={styles.checksList}>
              {results.checks?.map((check, i) => (
                <div key={check.id || i} style={{
                  ...styles.checkItem,
                  borderLeftColor: getStatusColor(check),
                }}>
                  <span style={styles.checkIcon}>{getStatusIcon(check)}</span>
                  <div style={styles.checkBody}>
                    <div style={styles.checkDesc}>{check.description}</div>
                    <div style={styles.checkValue}>
                      {check.value} {check.required ? `(required: ${check.required})` : ''}
                    </div>
                    {check.details && check.details.distanceM !== undefined && (
                      <div style={styles.checkDetail}>
                        Distance: {(check.details.distanceM / 1000).toFixed(1)}km | 
                        Duration: ~{Math.round(check.details.durationS / 60)}min | 
                        Endurance: ~{Math.round(check.details.enduranceS / 60)}min
                      </div>
                    )}
                  </div>
                  {check.blocking && <span style={styles.blockingTag}>BLOCKING</span>}
                </div>
              ))}
            </div>

            {!results.passed && handleForceComplete && (
              <div style={styles.forceNote}>
                Force arm bypasses safety checks like GPS or battery warnings, but the drone connection and mission data still need to be valid.
              </div>
            )}

            {/* Actions */}
            <div style={styles.actions}>
              <button onClick={() => { setResults(null); }} style={styles.retryBtn}>Re-run</button>
              {!results.passed && handleForceComplete && (
                <button onClick={() => handleForceComplete(acknowledgedManual)} style={styles.forceBtn}>
                  Force Arm & Start Mission
                </button>
              )}
              {results.passed && (
                <button onClick={handleProceed} style={styles.proceedBtn}>
                  Proceed to Start Mission
                </button>
              )}
              {!results.passed && (
                <button onClick={handleCancel} style={styles.cancelBtn}>Cancel</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
  },
  modal: {
    background: 'var(--bgPrimary)', borderRadius: '16px',
    width: '560px', maxWidth: '95vw', maxHeight: '85vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--borderSecondary)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, var(--bgSecondary) 0%, var(--bgTertiary) 100%)',
    borderBottom: '1px solid var(--borderSecondary)',
  },
  title: {
    margin: 0, fontSize: '16px', fontWeight: 800, letterSpacing: '0.3px',
    color: 'var(--accent)',
  },
  closeBtn: {
    background: 'var(--bgTertiary)', border: '1px solid var(--borderSecondary)',
    color: 'var(--textMuted)', fontSize: '16px', cursor: 'pointer',
    padding: '4px 10px', borderRadius: '6px', transition: 'all 0.2s', lineHeight: 1,
  },
  body: {
    padding: '18px 20px', overflowY: 'auto', flex: 1,
  },
  info: {
    fontSize: '13px', color: 'var(--textSecondary)', marginBottom: '18px',
    lineHeight: '1.6', padding: '12px 14px',
    background: 'var(--bgTertiary)', borderRadius: '10px',
    border: '1px solid var(--borderSecondary)',
  },
  manualSection: { marginBottom: '18px' },
  sectionTitle: {
    fontSize: '11px', fontWeight: 700, marginBottom: '10px',
    color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.8px',
    paddingBottom: '6px', borderBottom: '1px solid var(--borderSecondary)',
  },
  checkRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '9px 12px', cursor: 'pointer', fontSize: '13px',
    color: 'var(--textPrimary)', borderRadius: '8px',
    background: 'var(--bgSecondary)', marginBottom: '4px',
    border: '1px solid var(--borderSecondary)',
    transition: 'all 0.15s',
  },
  checkbox: {
    width: '16px', height: '16px', accentColor: 'var(--accent)',
    cursor: 'pointer',
  },
  runBtn: {
    width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, var(--accentSecondary) 0%, var(--accent) 100%)',
    color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    textTransform: 'uppercase', letterSpacing: '0.8px',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'opacity 0.2s, transform 0.15s',
  },
  error: {
    color: 'var(--danger)', fontSize: '13px', marginTop: '10px',
    textAlign: 'center', padding: '10px',
    background: 'color-mix(in srgb, var(--danger) 10%, transparent)', borderRadius: '8px',
  },
  summary: {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '14px 18px', borderRadius: '12px', border: '2px solid',
    marginBottom: '14px',
  },
  summaryStats: {
    fontSize: '12px', color: 'var(--textMuted)', marginTop: '3px',
    fontFamily: 'monospace',
  },
  blockingBox: {
    background: 'color-mix(in srgb, var(--danger) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 25%, transparent)',
    borderRadius: '10px', padding: '12px 16px', marginBottom: '14px',
    fontSize: '13px', color: 'var(--danger)',
  },
  forceNote: {
    marginBottom: '14px',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid color-mix(in srgb, var(--warning) 35%, transparent)',
    background: 'color-mix(in srgb, var(--warning) 8%, transparent)',
    color: 'var(--textSecondary)',
    fontSize: '12px',
    lineHeight: '1.5',
  },
  blockingList: { margin: '8px 0 0 18px', padding: 0, lineHeight: '1.6' },
  checksList: {
    display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '18px',
  },
  checkItem: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '10px 12px', borderRadius: '8px',
    background: 'var(--bgSecondary)', borderLeft: '3px solid',
    border: '1px solid var(--borderSecondary)', borderLeftWidth: '3px',
  },
  checkIcon: { fontSize: '16px', flexShrink: 0, marginTop: '1px' },
  checkBody: { flex: 1, minWidth: 0 },
  checkDesc: {
    fontSize: '13px', fontWeight: 600, color: 'var(--textPrimary)',
  },
  checkValue: {
    fontSize: '11px', color: 'var(--textMuted)', marginTop: '2px',
    fontFamily: 'monospace',
  },
  checkDetail: {
    fontSize: '11px', color: 'var(--textMuted)', marginTop: '3px',
    fontStyle: 'italic', padding: '4px 8px',
    background: 'var(--bgTertiary)', borderRadius: '4px',
  },
  blockingTag: {
    fontSize: '9px', fontWeight: 800, color: '#fff',
    background: 'var(--danger)', padding: '3px 8px',
    borderRadius: '4px', flexShrink: 0, textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  actions: {
    display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap',
  },
  retryBtn: {
    padding: '9px 18px', borderRadius: '8px',
    border: '1px solid var(--borderSecondary)',
    background: 'var(--bgSecondary)', color: 'var(--textPrimary)',
    cursor: 'pointer', fontWeight: 600, fontSize: '13px',
    transition: 'all 0.2s',
  },
  proceedBtn: {
    padding: '9px 22px', borderRadius: '8px', border: 'none',
    background: 'linear-gradient(135deg, var(--success) 0%, var(--successHover) 100%)',
    color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '13px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    boxShadow: '0 2px 8px color-mix(in srgb, var(--success) 30%, transparent)',
    transition: 'opacity 0.2s',
  },
  forceBtn: {
    padding: '9px 22px', borderRadius: '8px', border: 'none',
    background: 'linear-gradient(135deg, var(--warning) 0%, var(--danger) 100%)',
    color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: '13px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    boxShadow: '0 2px 8px color-mix(in srgb, var(--warning) 25%, transparent)',
    transition: 'opacity 0.2s',
  },
  cancelBtn: {
    padding: '9px 18px', borderRadius: '8px', border: 'none',
    background: 'var(--danger)', color: '#fff', cursor: 'pointer',
    fontWeight: 700, fontSize: '13px',
    transition: 'opacity 0.2s',
  },
};
